import {
    addDoc,
    collection,
    deleteDoc,
    doc,
    getDoc,
    getDocs,
    setDoc,
    type DocumentSnapshot,
    type QueryDocumentSnapshot,
} from "firebase/firestore";
import { db } from "@/lib/firebase";
import { errorEvents } from "@/lib/error-events";

// -----------------------------------------------------------------------------
// CMS "Customers" API — see plans/CMS.md
//
// Persistence is Firestore: one document per customer in the `customers`
// collection. The Firestore document id IS the `Company.id` (so `id` is not
// stored inside the document body). The exported signatures/DTOs are unchanged
// from the earlier localStorage prototype, so callers stay identical.
// -----------------------------------------------------------------------------

export const CUSTOMERS_COLLECTION = "customers";

export type Status =
    | "New"
    | "Contacted"
    | "In process"
    | "Attention needed"
    | "Won"
    | "Lost";

export interface NextAction {
    description: string;
    /** date-only, "YYYY-MM-DD" */
    dueDate: string;
}

/**
 * Number of offices the agency runs, as a coarse bucket. `4` is the open-ended
 * "4+" bucket (four or more offices); `null` means unset. Kept numeric so it
 * sorts naturally — see `sortCompanies` / `officeCountLabel` in `cms.ts`.
 */
export type OfficeCount = 1 | 2 | 3 | 4;

export interface Contact {
    id: string;
    name: string;
    title: string;
    phone: string;
    email: string;
    description: string;
    isPrimary: boolean;
}

/** Reference-only brand style block (displayed, never applied). */
export interface Style {
    /** hex or "" ("" = no color set) */
    primaryColor: string;
    /** hex or "" ("" = no color set) */
    secondaryColor: string;
    fontStyle: string;
    corners: "rounded" | "sharp";
}

export interface Company {
    id: string;
    name: string;
    /** normalized to a URL on save; null when unset */
    homepage: string | null;
    description: string;
    status: Status;
    /** date-only "YYYY-MM-DD", or null when never contacted */
    lastContacted: string | null;
    /** single open action, or null */
    nextAction: NextAction | null;
    /** number-of-offices bucket (1/2/3/4+), or null when unset */
    officeCount: OfficeCount | null;
    style: Style;
    contacts: Contact[];
    /** full ISO timestamp */
    createdAt: string;
    /** full ISO timestamp; bumped by this module on every write */
    updatedAt: string;
}

export interface CreateCompanyInput {
    name: string;
}

/** The document body stored in Firestore (everything except the id). */
export type CompanyDoc = Omit<Company, "id">;

// -----------------------------------------------------------------------------
// Date / id helpers
// -----------------------------------------------------------------------------

function pad2(n: number): string {
    return String(n).padStart(2, "0");
}

/** The viewer's LOCAL date as "YYYY-MM-DD". */
export function todayLocal(): string {
    const d = new Date();
    return `${d.getFullYear()}-${pad2(d.getMonth() + 1)}-${pad2(d.getDate())}`;
}

/** LOCAL date `days` from today as "YYYY-MM-DD" (used only for demo seeding). */
function localDateOffset(days: number): string {
    const d = new Date();
    d.setDate(d.getDate() + days);
    return `${d.getFullYear()}-${pad2(d.getMonth() + 1)}-${pad2(d.getDate())}`;
}

function nowISO(): string {
    return new Date().toISOString();
}

export function genId(): string {
    if (typeof globalThis.crypto?.randomUUID === "function") {
        return globalThis.crypto.randomUUID();
    }
    return `id-${Math.random().toString(36).slice(2)}-${Date.now().toString(36)}`;
}

export const DEFAULT_STYLE: Style = {
    primaryColor: "",
    secondaryColor: "",
    fontStyle: "",
    corners: "rounded",
};

// -----------------------------------------------------------------------------
// Pure domain helpers (no Firestore) — unit-testable in isolation.
// -----------------------------------------------------------------------------

/**
 * Build the document body for a brand-new customer with sensible defaults.
 * Throws if the name is blank. Returns the body WITHOUT an id (Firestore
 * assigns the id).
 */
export function buildNewCompany(input: CreateCompanyInput): CompanyDoc {
    const name = input?.name?.trim();
    if (!name) {
        throw new Error("Company name is required.");
    }
    const timestamp = nowISO();
    return {
        name,
        homepage: null,
        description: "",
        status: "New",
        lastContacted: null,
        nextAction: null,
        officeCount: null,
        style: { ...DEFAULT_STYLE },
        contacts: [],
        createdAt: timestamp,
        updatedAt: timestamp,
    };
}

/**
 * Shallow-merge `patch` field-by-field onto `existing`. Array fields
 * (`contacts`) are replaced wholesale. `id`/`createdAt` are preserved and
 * `updatedAt` is bumped.
 *
 * `lastContacted` auto-stamp: on the transition INTO `Contacted` (and only if
 * `lastContacted` is currently null and the caller did not set it explicitly),
 * stamp it with `todayLocal()`. No other transition touches it, and it never
 * overwrites an existing value.
 */
export function applyCompanyPatch(existing: Company, patch: Partial<Company>): Company {
    const effectivePatch: Partial<Company> = { ...patch };

    const enteringContacted =
        patch.status === "Contacted" && existing.status !== "Contacted";
    if (
        enteringContacted &&
        existing.lastContacted == null &&
        effectivePatch.lastContacted === undefined
    ) {
        effectivePatch.lastContacted = todayLocal();
    }

    return {
        ...existing,
        ...effectivePatch,
        id: existing.id,
        createdAt: existing.createdAt,
        updatedAt: nowISO(),
    };
}

// -----------------------------------------------------------------------------
// Firestore mapping
// -----------------------------------------------------------------------------

function fromSnapshot(
    snapshot: DocumentSnapshot | QueryDocumentSnapshot,
): Company {
    return { id: snapshot.id, ...(snapshot.data() as CompanyDoc) };
}

/** Strip the id off a Company to get the storable document body. */
function toDoc(company: Company): CompanyDoc {
    const { id: _id, ...rest } = company;
    return rest;
}

// -----------------------------------------------------------------------------
// API surface (Firestore-backed)
// -----------------------------------------------------------------------------

export async function listCompanies(): Promise<Company[]> {
    try {
        const snapshot = await getDocs(collection(db, CUSTOMERS_COLLECTION));
        return snapshot.docs.map(fromSnapshot);
    } catch (error) {
        const message = error instanceof Error ? error.message : "Fetching companies failed";
        errorEvents.emit(message, "Customers fetching error");
        throw error;
    }
}

export async function getCompany(id: string): Promise<Company> {
    try {
        const snapshot = await getDoc(doc(db, CUSTOMERS_COLLECTION, id));
        if (!snapshot.exists()) {
            throw new Error("Company not found.");
        }
        return fromSnapshot(snapshot);
    } catch (error) {
        const message = error instanceof Error ? error.message : "Fetching company failed";
        errorEvents.emit(message, "Customers fetching error");
        throw error;
    }
}

export async function createCompany(input: CreateCompanyInput): Promise<Company> {
    try {
        const data = buildNewCompany(input);
        const ref = await addDoc(collection(db, CUSTOMERS_COLLECTION), data);
        return { id: ref.id, ...data };
    } catch (error) {
        const message = error instanceof Error ? error.message : "Creating company failed";
        errorEvents.emit(message, "Customers creation error");
        throw error;
    }
}

export async function updateCompany(id: string, patch: Partial<Company>): Promise<Company> {
    try {
        const ref = doc(db, CUSTOMERS_COLLECTION, id);
        const snapshot = await getDoc(ref);
        if (!snapshot.exists()) {
            throw new Error("Company not found.");
        }
        const updated = applyCompanyPatch(fromSnapshot(snapshot), patch);
        await setDoc(ref, toDoc(updated));
        return updated;
    } catch (error) {
        const message = error instanceof Error ? error.message : "Updating company failed";
        errorEvents.emit(message, "Customers update error");
        throw error;
    }
}

export async function deleteCompany(id: string): Promise<void> {
    try {
        await deleteDoc(doc(db, CUSTOMERS_COLLECTION, id));
    } catch (error) {
        const message = error instanceof Error ? error.message : "Deleting company failed";
        errorEvents.emit(message, "Customers deletion error");
        throw error;
    }
}

// -----------------------------------------------------------------------------
// Optional demo seeding — NOT called automatically (Firestore is shared,
// persistent state). Call once to populate an empty `customers` collection with
// ~7 demo companies across statuses. No-ops if the collection already has docs.
// Seed ids are stable so detail links survive reloads.
// -----------------------------------------------------------------------------

export async function seedDemoCompanies(): Promise<number> {
    const existing = await getDocs(collection(db, CUSTOMERS_COLLECTION));
    if (!existing.empty) {
        return 0;
    }
    const demo = demoCompanies();
    await Promise.all(
        demo.map((company) =>
            setDoc(doc(db, CUSTOMERS_COLLECTION, company.id), toDoc(company)),
        ),
    );
    return demo.length;
}

function demoCompanies(): Company[] {
    const t = nowISO();
    return [
        {
            id: "seed-nordic",
            name: "Nordic Estate Group",
            homepage: "https://nordicestate.se",
            description: "Large agency chain in Stockholm; multiple offices.",
            status: "In process",
            lastContacted: localDateOffset(-24),
            nextAction: { description: "Send follow-up proposal", dueDate: localDateOffset(-3) },
            officeCount: 4,
            style: {
                primaryColor: "#1d4ed8",
                secondaryColor: "#f59e0b",
                fontStyle: "Poppins / sans-serif",
                corners: "rounded",
            },
            contacts: [
                {
                    id: "seed-nordic-c1",
                    name: "Anna Lindqvist",
                    title: "Head of Sales",
                    phone: "070-123 45 67",
                    email: "anna.lindqvist@nordicestate.se",
                    description: "Decision maker; prefers email.",
                    isPrimary: true,
                },
                {
                    id: "seed-nordic-c2",
                    name: "Erik Holm",
                    title: "Agent",
                    phone: "+46 8 555 010",
                    email: "erik.holm@nordicestate.se",
                    description: "",
                    isPrimary: false,
                },
            ],
            createdAt: t,
            updatedAt: t,
        },
        {
            id: "seed-sundberg",
            name: "Sundberg Fastigheter",
            homepage: "https://sundbergfastigheter.se",
            description: "Boutique agency, west coast.",
            status: "Contacted",
            lastContacted: localDateOffset(-6),
            nextAction: { description: "Call to book a demo", dueDate: todayLocal() },
            officeCount: 1,
            style: {
                primaryColor: "#0f766e",
                secondaryColor: "",
                fontStyle: "Georgia / serif",
                corners: "sharp",
            },
            contacts: [
                {
                    id: "seed-sundberg-c1",
                    name: "Maria Sundberg",
                    title: "Owner",
                    phone: "0730-99 88 77",
                    email: "maria@sundbergfastigheter.se",
                    description: "Founder.",
                    isPrimary: true,
                },
            ],
            createdAt: t,
            updatedAt: t,
        },
        {
            id: "seed-brightmove",
            name: "Bright Move Realty",
            homepage: null,
            description: "New lead from the spring expo.",
            status: "New",
            lastContacted: null,
            nextAction: null,
            officeCount: null,
            style: { ...DEFAULT_STYLE },
            contacts: [
                {
                    id: "seed-brightmove-c1",
                    name: "Johan Persson",
                    title: "",
                    phone: "070 200 30 40",
                    email: "johan@brightmove.se",
                    description: "",
                    isPrimary: true,
                },
            ],
            createdAt: t,
            updatedAt: t,
        },
        {
            id: "seed-kustlinjen",
            name: "Kustlinjen Mäkleri",
            homepage: "https://kustlinjen.se",
            description: "Coastal properties specialist.",
            status: "Attention needed",
            lastContacted: localDateOffset(-40),
            nextAction: { description: "Re-engage after silence", dueDate: localDateOffset(6) },
            officeCount: 2,
            style: {
                primaryColor: "#be123c",
                secondaryColor: "#334155",
                fontStyle: "Inter / sans-serif",
                corners: "rounded",
            },
            contacts: [
                {
                    id: "seed-kustlinjen-c1",
                    name: "Sofia Berg",
                    title: "Broker",
                    phone: "076-311 22 33",
                    email: "sofia.berg@kustlinjen.se",
                    description: "Went quiet after last call.",
                    isPrimary: true,
                },
                {
                    id: "seed-kustlinjen-c2",
                    name: "Petter Nyström",
                    title: "Assistant",
                    phone: "076-311 22 34",
                    email: "petter@kustlinjen.se",
                    description: "",
                    isPrimary: false,
                },
            ],
            createdAt: t,
            updatedAt: t,
        },
        {
            id: "seed-urbannest",
            name: "Urban Nest Realtors",
            homepage: "https://urbannest.se",
            description: "City-centre apartments.",
            status: "In process",
            lastContacted: localDateOffset(-12),
            nextAction: { description: "Prepare contract draft", dueDate: localDateOffset(14) },
            officeCount: 3,
            style: {
                primaryColor: "#7c3aed",
                secondaryColor: "#22d3ee",
                fontStyle: "Montserrat / sans-serif",
                corners: "rounded",
            },
            contacts: [
                {
                    id: "seed-urbannest-c1",
                    name: "Lisa Ek",
                    title: "Managing Director",
                    phone: "070-777 66 55",
                    email: "lisa.ek@urbannest.se",
                    description: "",
                    isPrimary: true,
                },
            ],
            createdAt: t,
            updatedAt: t,
        },
        {
            id: "seed-fjord",
            name: "Fjord & Co Realty",
            homepage: "https://fjordco.no",
            description: "Signed last quarter — reference customer.",
            status: "Won",
            lastContacted: localDateOffset(-60),
            nextAction: { description: "Quarterly check-in", dueDate: localDateOffset(-2) },
            officeCount: 4,
            style: {
                primaryColor: "#059669",
                secondaryColor: "#065f46",
                fontStyle: "Poppins / sans-serif",
                corners: "rounded",
            },
            contacts: [
                {
                    id: "seed-fjord-c1",
                    name: "Kari Nordmann",
                    title: "CEO",
                    phone: "+47 900 12 345",
                    email: "kari@fjordco.no",
                    description: "Happy customer.",
                    isPrimary: true,
                },
            ],
            createdAt: t,
            updatedAt: t,
        },
        {
            id: "seed-oldtown",
            name: "Old Town Brokers",
            homepage: null,
            description: "Declined — went with a competitor.",
            status: "Lost",
            lastContacted: localDateOffset(-90),
            nextAction: null,
            officeCount: null,
            style: { ...DEFAULT_STYLE },
            contacts: [
                {
                    id: "seed-oldtown-c1",
                    name: "Gustav Åberg",
                    title: "Partner",
                    phone: "08-123 456",
                    email: "gustav@oldtownbrokers.se",
                    description: "",
                    isPrimary: true,
                },
            ],
            createdAt: t,
            updatedAt: t,
        },
    ];
}
